<app-page-layout>
    <sh-breadcrumbs breadcrumbs>
        <sh-breadcrumb-item [label]="'breadcrumbs.home' | translate" href="/"></sh-breadcrumb-item>
        <sh-breadcrumb-item
            [label]="'out-of-office-overview.title' | translate"
            href="/settings/out-of-office-calendar"
        ></sh-breadcrumb-item>
    </sh-breadcrumbs>

    <h1 title class="text-2xl font-bold font-sh-bree-headline text-shui-text-strong">
        {{ 'out-of-office-overview.title' | translate }}
    </h1>

    <div class="flex w-full flex-1 min-h-0 flex-col">
        <div class="grid grid-cols-12 w-full gap-4 flex-1 min-h-0 overflow-hidden">
            <section
                class="col-span-8 flex h-full min-h-0 flex-col bg-shui-base-3 rounded-md border border-shui-ui-2 pt-2 overflow-hidden"
            >
                <div class="grid grid-cols-12 items-center px-4 py-2 flex-shrink-0">
                    <div class="col-span-4 flex justify-start">
                        <button
                            type="button"
                            id="team-filter-trigger"
                            class="flex items-center gap-1 bg-transparent border-none text-shui-text-strong cursor-pointer"
                        >
                            <sh-icon icon="user-group" size="xs"></sh-icon>
                            <sh-text size="body-1">{{ 'out-of-office-overview.team' | translate }}</sh-text>
                            <sh-icon icon="arrow-face-down-s"></sh-icon>
                        </button>
                        <sh-popover target="team-filter-trigger" position="bottom-left">
                            @for (team of teams(); track team.id) {
                                <sh-menu-item
                                    [label]="team.name"
                                    checkbox
                                    [active]="isTeamSelected(team)"
                                    (click)="onTeamSelectionChange(team)"
                                ></sh-menu-item>
                            }
                        </sh-popover>
                    </div>

                    <div class="col-span-4 flex items-center justify-center">
                        <span class="flex items-center gap-1">
                            <sh-icon icon="arrow-line-left-s" button (click)="onPreviousDate()"></sh-icon>
                            <span class="px-4 whitespace-nowrap text-shui-text-strong">{{ dateDisplay() }}</span>
                            <sh-icon icon="arrow-line-right-s" button (click)="onNextDate()"></sh-icon>
                        </span>
                    </div>

                    <div class="col-span-4 flex justify-end">
                        <button
                            type="button"
                            id="view-selector-trigger"
                            class="flex items-center gap-1 bg-transparent border-none text-shui-text-strong cursor-pointer"
                        >
                            <sh-icon
                                icon="{{ calendarView() === 'month' ? 'calendar-monthly' : 'calendar-weekly' }}"
                                size="xs"
                            ></sh-icon>
                            <sh-text size="body-1">{{
                                'out-of-office-overview.' + calendarView() | translate
                            }}</sh-text>
                            <sh-icon icon="arrow-face-down-s"></sh-icon>
                        </button>
                        <sh-popover target="view-selector-trigger" position="bottom-right">
                            <sh-menu-item
                                [label]="'out-of-office-overview.month' | translate"
                                icon="calendar-monthly"
                                [toggle]="false"
                                [active]="calendarView() === 'month'"
                                (click)="setCalendarView('month')"
                            ></sh-menu-item>
                            <sh-menu-item
                                [label]="'out-of-office-overview.week' | translate"
                                icon="calendar-weekly"
                                [toggle]="false"
                                [active]="calendarView() === 'week'"
                                (click)="setCalendarView('week')"
                            ></sh-menu-item>
                        </sh-popover>
                    </div>
                </div>

                <app-out-of-office-overview-grid
                    class="flex-1 min-h-0 overflow-auto"
                    [isDataLoading]="isLoading()"
                    [calendarView]="calendarView()"
                    [date]="date()"
                    [userEvents]="userEvents()"
                    [selectedUserEvent]="selectedUserEvent()"
                ></app-out-of-office-overview-grid>
            </section>

            <div class="col-span-4 bg-shui-base-3 rounded-md h-full min-h-0 overflow-auto p-6">
                @if (selectedEntry()) {
                    <app-out-of-office-entry-detail
                        [entry]="selectedEntry()!"
                        [isAdmin]="isAdmin()"
                        [isDeleting]="isDeleting()"
                        (deleteClicked)="onDeleteDelegation()"
                        (cancelClicked)="onCancelSelection()"
                    ></app-out-of-office-entry-detail>
                }

                <div class="grid grid-cols-12 px-2 mt-4">
                    <div class="col-span-5">
                        <sh-text size="title-1" color="primary">
                            {{ 'out-of-office-overview.out-of-office' | translate }}
                        </sh-text>
                    </div>
                    <div class="col-span-3 text-center">
                        <sh-text size="title-1" color="primary">
                            {{ 'out-of-office-overview.days-off' | translate }}
                        </sh-text>
                    </div>
                    <div class="col-span-4">
                        <sh-text size="title-1" color="primary">
                            {{ 'out-of-office-overview.coverage' | translate }}
                        </sh-text>
                    </div>
                </div>
                <div class="h-px w-full bg-shui-base-1 my-4 mt-2"></div>
                <div class="flex flex-col gap-2 mt-2">
                    @if (isLoading()) {
                        <div class="bg-shui-base-2 h-10 rounded-md animate-pulse"></div>
                        <div class="bg-shui-base-2 h-10 rounded-md animate-pulse opacity-90"></div>
                        <div class="bg-shui-base-2 h-10 rounded-md animate-pulse opacity-80"></div>
                    }

                    @for (entry of entries(); track entry.id) {
                        <button
                            type="button"
                            class="grid grid-cols-12 w-full cursor-pointer rounded-md border-none p-2 text-left transition-colors"
                            [class.bg-shui-base-2]="selectedEntry()?.id !== entry.id"
                            [class.bg-shui-tint-active-150]="selectedEntry()?.id === entry.id"
                            (click)="onSelectDelegation(entry)"
                        >
                            <div class="col-span-5 flex flex-col overflow-hidden">
                                <sh-text size="body-1" color="primary" class="truncate">{{ entry.userName }}</sh-text>
                                <sh-text size="body-2" color="secondary" class="truncate">{{
                                    entry.dateRange
                                }}</sh-text>
                            </div>
                            <div class="col-span-3 flex items-center justify-center">
                                <sh-text size="body-1" color="primary">{{ entry.daysOff }}</sh-text>
                            </div>
                            <div class="col-span-4 flex items-center overflow-hidden">
                                <sh-text size="body-1" color="primary" class="truncate">{{
                                    entry.coverageName
                                }}</sh-text>
                            </div>
                        </button>
                    } @empty {
                        @if (!isLoading()) {
                            <sh-text size="body-1" color="secondary" class="my-8 text-center block">
                                {{ 'out-of-office-overview.no-delegations' | translate }}
                            </sh-text>
                        }
                    }
                </div>
            </div>
        </div>
    </div>
</app-page-layout>
